/**
 * Progress-thumbnail rendering math (#195).
 *
 * The home screen shows each project as a tiny, non-interactive thumbnail of just
 * its *completed* stitches on the fabric colour, so progress reads at a glance.
 * This module is the pure cell→colour resolution, kept free of canvas/Vue so it's
 * unit-testable in isolation; PatternPreview's completed-only mode paints what it
 * returns.
 */
import type { Pattern } from '@cross-my-heart/shared'

/** A completed cell from a project. `threadIndex` is the thread actually stitched
 * (#189); legacy completions omit it and fall back to the chart's thread. */
export interface CompletedCell {
  x: number
  y: number
  threadIndex?: number
}

/** A cell to paint on the thumbnail: chart position + resolved CSS colour. */
export interface PaintedCell {
  x: number
  y: number
  color: string
}

/**
 * Resolve the cells to paint for a completed-only thumbnail. Each completion is
 * painted in its actual thread colour — the thread it recorded (#189), or the
 * chart's thread at that position for legacy completions. The `??` guards index 0
 * (a falsy-but-valid palette index). Completions with neither a recorded thread
 * nor a charted stitch underneath (e.g. the pattern shrank) are skipped rather
 * than painted in the fallback grey.
 */
export function completedCells(pattern: Pattern, completions: CompletedCell[]): PaintedCell[] {
  const threadColor = new Map<number, string>()
  for (const thread of pattern.palette) threadColor.set(thread.index, thread.color)

  const chartedAt = new Map<string, number>()
  for (const stitch of pattern.stitches) {
    const key = `${stitch.x}_${stitch.y}`
    if (!chartedAt.has(key)) chartedAt.set(key, stitch.threadIndex)
  }

  const painted: PaintedCell[] = []
  for (const cell of completions) {
    const index = cell.threadIndex ?? chartedAt.get(`${cell.x}_${cell.y}`)
    if (index === undefined) continue
    painted.push({ x: cell.x, y: cell.y, color: threadColor.get(index) ?? '#888888' })
  }
  return painted
}
